import { Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';

import {
  APP_VERSION_APP_LABEL_MESSAGES,
  APP_VERSION_APP_ORDER,
  APP_VERSION_PLATFORM_ORDER,
} from '@constants/app-version';

import { useAppVersionSummaries } from '@hooks/api/app-version';

import type { AppVersionSummaryFieldsFragment } from 'generated/app-version.generated';
import type { AppVersionApp } from 'generated/types';

import AppSection from './AppSection';

const messages = defineMessages({
  empty: {
    id: 'admin.configurations.empty',
    defaultMessage: 'No app version data available',
  },
  loadError: {
    id: 'admin.configurations.loadError',
    defaultMessage: 'Could not load app version data. Please try again.',
  },
});

function sortSummariesForApp(
  summaries: AppVersionSummaryFieldsFragment[]
): AppVersionSummaryFieldsFragment[] {
  return [...summaries].sort(
    (left, right) =>
      APP_VERSION_PLATFORM_ORDER.indexOf(left.platform) -
      APP_VERSION_PLATFORM_ORDER.indexOf(right.platform)
  );
}

function ConfigurationsMain() {
  const { formatMessage } = useIntl();
  const { data, loading, error } = useAppVersionSummaries();
  const summaries = useMemo(
    () => data?.listAppVersionSummaries ?? [],
    [data?.listAppVersionSummaries]
  );

  const sections = useMemo(() => {
    const byApp = new Map<AppVersionApp, AppVersionSummaryFieldsFragment[]>();

    APP_VERSION_APP_ORDER.forEach((app) => {
      byApp.set(app, []);
    });

    summaries.forEach((summary) => {
      const existing = byApp.get(summary.app) ?? [];
      existing.push(summary);
      byApp.set(summary.app, existing);
    });

    return APP_VERSION_APP_ORDER.map((app) => ({
      app,
      label: formatMessage(APP_VERSION_APP_LABEL_MESSAGES[app]),
      summaries: sortSummariesForApp(byApp.get(app) ?? []),
    }));
  }, [formatMessage, summaries]);

  return (
    <ContentLoader loading={loading}>
      {error && !loading ? (
        <Typography color="error" data-testid="configurations-load-error">
          {formatMessage(messages.loadError)}
        </Typography>
      ) : summaries.length === 0 && !loading ? (
        <Typography color="text.secondary">
          {formatMessage(messages.empty)}
        </Typography>
      ) : (
        <Stack spacing={3}>
          {sections.map(({ app, label, summaries: appSummaries }) => (
            <AppSection
              key={app}
              app={app}
              label={label}
              summaries={appSummaries}
            />
          ))}
        </Stack>
      )}
    </ContentLoader>
  );
}

export default ConfigurationsMain;
